import NumberFormat, { NumberFormatValues } from 'react-number-format';
import { ForwardedRef, forwardRef } from 'react';
import { useIntl } from 'react-intl';

interface EventStub {
    target: {
        id: string;
        name: string;
        value?: number;
    };
}

interface NumberFormatCustomProps {
    id: string;
    name: string;
    fixedDecimalScale?: boolean;
    decimalScale?: number;
    onChange: (event: EventStub) => void;
    onBlur: (event: EventStub) => void;
    noFractionalNumbers?: boolean;
    noNegativeNumbers?: boolean;
    placeholder?: string;
    prefix?: string;
    suffix?: string;
    hasThousandSeparator?: boolean;
}

function NumericTextfieldRef(props: NumberFormatCustomProps, ref: ForwardedRef<NumberFormat<NumberFormatCustomProps>>) {
    const {
        id,
        name,
        onChange,
        onBlur,
        fixedDecimalScale,
        decimalScale,
        noFractionalNumbers,
        noNegativeNumbers,
        placeholder,
        hasThousandSeparator = true,
        ...other
    } = props;

    const intl = useIntl();
    const decimalNumber = 1000.1;
    const parts = intl.formatNumberToParts(decimalNumber);
    // HINT: when mergin with theme-next, don't forget to add thousandSeparator to the props
    const thousandSeparator = hasThousandSeparator ? parts.find(part => part.type === 'group')?.value ?? '.' : false;
    const decimalSeparator = parts.find(part => part.type === 'decimal')?.value ?? ',';

    const handleValueChange = (values: NumberFormatValues) => {
        const value = values.floatValue;
        onChange({ target: { id, name, value } });
    };

    const handleBlur = () => {
        onBlur({ target: { id, name } });
    };

    return (
        <NumberFormat
            {...other}
            id={id}
            name={name}
            getInputRef={ref}
            thousandSeparator={thousandSeparator}
            decimalSeparator={decimalSeparator}
            decimalScale={noFractionalNumbers ? 0 : decimalScale}
            fixedDecimalScale={fixedDecimalScale}
            allowNegative={!noNegativeNumbers}
            onValueChange={handleValueChange}
            onBlur={handleBlur}
            placeholder={placeholder || '0'}
        />
    );
}

export const NumericTextfield = forwardRef(NumericTextfieldRef);
